/**
 * Validation d'une ligne de brouillon de facture, côté interface. Les bornes
 * reprennent le schéma de l'API (`ligneEntreeSchema` : quantité entière de 1 à
 * 9999, remise en points de base de 0 à 10 000) ; le reste est du bon sens :
 * pas de prix négatif, pas de montant au-delà de 100 M€. Avant, l'écran
 * ramenait en silence la quantité à 1 et la remise à 100 : on refuse et on dit
 * pourquoi.
 */

export const QUANTITE_MAX = 9999;
/** Un prix unitaire, comme un total de ligne, ne dépasse pas 100 M€. */
export const PRIX_UNITAIRE_MAX_CENTIMES = 10_000_000_000;

export type CleErreurLigne =
  | 'regie:ventes.fiche.erreurs.quantite'
  | 'regie:ventes.fiche.erreurs.prixNegatif'
  | 'regie:ventes.fiche.erreurs.prixTropEleve'
  | 'regie:ventes.fiche.erreurs.remise';

export interface ErreursLigne {
  quantite?: CleErreurLigne;
  prix?: CleErreurLigne;
  remise?: CleErreurLigne;
}

export interface SaisieLigne {
  /** Texte saisi : un champ vidé n'est pas « 1 ». */
  readonly quantite: string;
  readonly prixUnitaireCentimes: number;
  /** Texte saisi, en pourcents ; la virgule vaut le point. */
  readonly remisePourcent: string;
}

export function validerLigne(saisie: SaisieLigne): ErreursLigne {
  const erreurs: ErreursLigne = {};

  const quantiteTexte = saisie.quantite.trim();
  const quantiteValide =
    /^\d+$/.test(quantiteTexte) &&
    Number(quantiteTexte) >= 1 &&
    Number(quantiteTexte) <= QUANTITE_MAX;
  if (!quantiteValide) erreurs.quantite = 'regie:ventes.fiche.erreurs.quantite';

  if (saisie.prixUnitaireCentimes < 0) {
    erreurs.prix = 'regie:ventes.fiche.erreurs.prixNegatif';
  } else if (
    saisie.prixUnitaireCentimes > PRIX_UNITAIRE_MAX_CENTIMES ||
    (quantiteValide &&
      saisie.prixUnitaireCentimes * Number(quantiteTexte) > PRIX_UNITAIRE_MAX_CENTIMES)
  ) {
    erreurs.prix = 'regie:ventes.fiche.erreurs.prixTropEleve';
  }

  const remiseTexte = saisie.remisePourcent.trim().replace(',', '.');
  const remiseValide =
    /^\d+(?:\.\d{1,2})?$/.test(remiseTexte) &&
    Number(remiseTexte) >= 0 &&
    Number(remiseTexte) <= 100;
  if (!remiseValide) erreurs.remise = 'regie:ventes.fiche.erreurs.remise';

  return erreurs;
}
